import { useState } from "react";
import { View, Text } from "../base";
import { Icon } from "../base/Icon";
import { JsonViewer } from "../base/JsonViewer";
import { SpanDetailTabs, TabDefinition } from "./SpanDetailTabs";
import { RawSpanDetailView, RawSpanData } from "./RawSpanDetailView";
import { safeString } from "../../utils/strings";

export interface BackgroundSpanData {
  name: string;
  outputEvents: BackgroundOutputEvent[];
}

export interface BackgroundOutputEvent {
  name: string;
  data: Record<string, unknown>;
  timestamp?: string;
  turn?: number;
  background?: boolean;
  background_task_name?: string | null;
}

// Get icon and color for event type
const getEventStyle = (eventName: string): { icon: string; color: string; bgColor: string; borderColor: string } => {
  switch (eventName) {
    case "text_to_speech":
      return {
        icon: "volume-up",
        color: "text-blue-500 dark:text-blue-400",
        bgColor: "bg-blue-50 dark:bg-blue-900/20",
        borderColor: "border-blue-200 dark:border-blue-800",
      };
    case "log":
      return {
        icon: "message",
        color: "text-gray-500 dark:text-gray-400",
        bgColor: "bg-gray-50 dark:bg-gray-800",
        borderColor: "border-gray-200 dark:border-gray-700",
      };
    case "audio":
      return {
        icon: "music",
        color: "text-purple-500 dark:text-purple-400",
        bgColor: "bg-purple-50 dark:bg-purple-900/20",
        borderColor: "border-purple-200 dark:border-purple-800",
      };
    case "stop":
      return {
        icon: "stop",
        color: "text-red-500 dark:text-red-400",
        bgColor: "bg-red-50 dark:bg-red-900/20",
        borderColor: "border-red-200 dark:border-red-800",
      };
    default:
      return {
        icon: "code",
        color: "text-gray-500 dark:text-gray-400",
        bgColor: "bg-gray-50 dark:bg-gray-800",
        borderColor: "border-gray-200 dark:border-gray-700",
      };
  }
};

// Get summary text for an output event
const getEventSummary = (event: BackgroundOutputEvent): string => {
  const data = event.data;
  switch (event.name) {
    case "text_to_speech": {
      return safeString(data.text, "") || "";
    }
    case "log": {
      const msg = safeString(data.message, "") || "";
      return msg.length > 150 ? msg.substring(0, 150) + "..." : msg;
    }
    case "audio":
      return safeString(data.path, "") || "";
    case "stop":
      return safeString(data.closing_speech, "Session ended") || "Session ended";
    default:
      return JSON.stringify(data).substring(0, 100);
  }
};

const OutputEventCard = ({ event }: { event: BackgroundOutputEvent }) => {
  const [expanded, setExpanded] = useState(false);
  const style = getEventStyle(event.name);
  const summary = getEventSummary(event);

  return (
    <View className={`rounded-lg border ${style.borderColor} ${style.bgColor} overflow-hidden`}>
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex flex-row items-start gap-2 p-2 text-left hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
      >
        <Icon
          name={style.icon}
          size={14}
          className={`${style.color} mt-0.5 flex-shrink-0`}
        />
        <View className="flex-1 min-w-0">
          <View className="flex flex-row items-center gap-2">
            <Text className={`text-xs font-medium ${style.color}`}>{event.name}</Text>
          </View>
          <Text className="text-sm text-gray-700 dark:text-gray-300 truncate">{summary}</Text>
        </View>
        <Icon
          name={expanded ? "chevron-up" : "chevron-down"}
          size={14}
          className="text-gray-400 flex-shrink-0 mt-0.5"
        />
      </button>
      {expanded && (
        <View className="border-t border-gray-200 dark:border-gray-700 p-2">
          <JsonViewer
            data={event.data}
            collapsed={3}
          />
        </View>
      )}
    </View>
  );
};

const BackgroundContent = ({ data }: { data: BackgroundSpanData }) => {
  return (
    <View className="space-y-4">
      {/* Task Name */}
      <View className="flex flex-row items-center gap-2">
        <Icon
          name="clock"
          size={20}
          className="text-purple-500 dark:text-purple-400"
        />
        <Text className="text-lg font-medium text-gray-800 dark:text-gray-200">{data.name}</Text>
      </View>

      {/* Output Events */}
      {data.outputEvents.length > 0 && (
        <View>
          <View className="flex flex-row items-center gap-2 mb-2">
            <Icon
              name="data-array"
              size={14}
              className="text-purple-500 dark:text-purple-400"
            />
            <Text className="text-sm font-medium text-gray-700 dark:text-gray-300">Outputs ({data.outputEvents.length})</Text>
          </View>
          <View className="space-y-2">
            {data.outputEvents.map((outputEvent, index) => (
              <OutputEventCard
                key={index}
                event={outputEvent}
              />
            ))}
          </View>
        </View>
      )}

      {data.outputEvents.length === 0 && (
        <View className="flex flex-row items-center gap-2 py-2">
          <Icon
            name="info"
            size={14}
            className="text-gray-400"
          />
          <Text className="text-sm text-gray-500 dark:text-gray-400">No output events</Text>
        </View>
      )}
    </View>
  );
};

interface BackgroundSpanDetailViewProps {
  data: BackgroundSpanData;
  rawData: RawSpanData;
  className?: string;
}

const tabs: TabDefinition[] = [
  { id: "background", label: "Background", color: "purple" },
  { id: "attributes", label: "Attributes", color: "gray" },
];

export const BackgroundSpanDetailView = ({ data, rawData, className }: BackgroundSpanDetailViewProps) => {
  const [activeTab, setActiveTab] = useState("background");

  return (
    <View className={`flex flex-col overflow-hidden h-full ${className || ""}`}>
      <SpanDetailTabs
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={setActiveTab}
      />
      <View className="flex-1 overflow-y-auto p-4">
        {activeTab === "background" ? <BackgroundContent data={data} /> : <RawSpanDetailView data={rawData} />}
      </View>
    </View>
  );
};

export const parseBackgroundSpanData = (span: { name: string; attributes: unknown }): BackgroundSpanData | null => {
  const attributes = span.attributes as Record<string, unknown>;
  const outputValue = attributes["output.value"];

  // Extract task name from span name (e.g., "background: task-name" → "task-name")
  const name = span.name.startsWith("background: ") ? span.name.substring("background: ".length) : span.name;

  let outputEvents: BackgroundOutputEvent[] = [];
  if (typeof outputValue === "string") {
    try {
      outputEvents = JSON.parse(outputValue) as BackgroundOutputEvent[];
    } catch {
      // Output may not be valid JSON array
    }
  }

  return { name, outputEvents };
};
